<template>
  <div class="base-table">
    <el-table
      :height="height"
      :stripe="stripe"
      v-ps="'.el-table__body-wrapper'"
      :ref="ref"
      :data="showData"
      :border="border"
      :class="[{ 'show-pointer': showPointer }, 'line-height-level-' + lineHeightLevel]"
      v-on="$listeners"
      header-row-class-name="header-row"
      :tree-props="treeProps"
      :min-col-width="70"
      row-key="id"
      @select-all="selectAll"
      @select="select"
      @selection-change="selectionChange"
      @expand-change="expandChange"
      v-bind="$attrs"
    >
      <slot></slot>
    </el-table>
    <slot name="summary"></slot>
    <div class="pagination-wrap" v-if="showPagination">
      <el-pagination
        class="table-pagination"
        background
        layout="prev, pager, next, jumper, total"
        :total="pageInfo.total"
        :current-page.sync="pageInfo.currentPage"
        :page-size="pageInfo.pageSize"
        @current-change="pageChange"
      ></el-pagination>

      <base-dropdown
        ref="levelDropdown"
        width="150"
        v-if="showLineHeightBox"
        placement="top-start"
        class="pointer"
      >
        <template v-slot:reference>
          <div class="change-size">
            <svg-icon :icon-class="'line-height-' + lineHeightLevel"></svg-icon>
          </div>
        </template>
        <div
          v-for="i in 3"
          :key="i"
          @click="selectLineHeight(i)"
          :class="['command-wrap', 'pointer', { 'is-active': lineHeightLevel == i }]"
        >
          <svg-icon :icon-class="'line-height-' + i"></svg-icon>
          {{ ["紧凑", "舒适", "宽敞"][i - 1] }}
        </div>
      </base-dropdown>
    </div>
  </div>
</template>

<script>
import BaseDropdown from "@/components/UIComponents/Dropdown"
import elementResizeDetectorMaker from "element-resize-detector"

export default {
  name: "base-table",
  components: {
    BaseDropdown
  },
  props: {
    stripe: {
      type: Boolean,
      default: false
    },
    border: {
      type: Boolean,
      default: true
    },
    height: {
      type: [String, Number],
      default: "100%"
    },
    data: Array,
    showPointer: {
      type: Boolean,
      default: true
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    // 前端分页
    frontendPagination: Boolean,
    showLineHeightBox: {
      type: Boolean,
      default: false
    },
    pageInfo: {
      type: Object,
      default: () => ({ total: 0, currentPage: 1, pageSize: 20 })
    },
    initialLineHeightLevel: String
  },
  data() {
    return {
      ref: "elTable",
      treeProps: { children: "children", hasChildren: "hasChildren" },
      debouncedEmitSelectionChange: null,
      tableBodyWrapper: null
    }
  },
  computed: {
    showData() {
      if (!this.showPagination || !this.frontendPagination) return this.data
      return this.data.slice(
        (this.pageInfo.currentPage - 1) * this.pageInfo.pageSize,
        this.pageInfo.currentPage * this.pageInfo.pageSize
      )
    },
    lineHeightLevel() {
      return this.initialLineHeightLevel || "2"
    }
  },
  created() {
    this.debouncedEmitSelectionChange = this._.debounce(this.emitSelectionChange, 100, {
      leading: false,
      trailing: true
    })
  },
  mounted() {
    this.tableBodyWrapper = this.$el.querySelector(".el-table .el-table__body-wrapper")
    this.tableBodyWrapper.addEventListener(
      "ps-scroll-y",
      this._.throttle(() => this.$emit("scroll"), 200)
    )
    let erd = elementResizeDetectorMaker()
    erd.listenTo(
      this.tableBodyWrapper,
      this._.debounce(() => {
        // 如果表格高度变化，需要手动更新一下ps，否则会出现滚动错位的情况
        this.tableBodyWrapper._ps_.update()
      }, 100)
    )
  },
  methods: {
    getElTable() {
      return this.$refs[this.ref]
    },
    selectLineHeight(level) {
      this.$emit("initial-line-height-level", level)
      this.$refs.levelDropdown.close()
    },
    // 手动勾选数据行
    select(selection, row) {
      // 判断当前行是否选中
      const selected = selection.some(item => item === row)
      // 处理所有子级
      this.selectChildren(row, selected)
    },
    selectAll(selection) {
      // tableData 第一层只要有在 selection 里面就是全选
      const isSelect = this.data.some(item => selection.includes(item))
      if (isSelect) {
        selection.forEach(item => {
          this.selectChildren(item, isSelect)
        })
      } else {
        this.data.forEach(item => {
          this.selectChildren(item, isSelect)
        })
      }
    },
    selectChildren(row, selected) {
      if (row[this.treeProps.children] && Array.isArray(row[this.treeProps.children])) {
        row[this.treeProps.children].forEach(item => {
          this.toggleSelection(item, selected)
          this.selectChildren(item, selected)
        })
      }
    },
    selectionChange(selection) {
      this.debouncedEmitSelectionChange(selection)
    },
    emitSelectionChange(selection) {
      this.$emit("selection-change", selection)
    },
    toggleSelection(row, select) {
      row && this.$nextTick(() => this.getElTable()?.toggleRowSelection(row, select))
    },
    toggleRowExpansion(row, expand) {
      row && this.$nextTick(() => this.getElTable()?.toggleRowExpansion(row, expand))
    },
    expandChange() {
      this.$nextTick(() => {
        this.tableBodyWrapper?._ps_?.update()
      })
    },
    pageChange(event) {
      this.$emit("page-change", event)
      this.tableBodyWrapper.scrollTop = 0
    }
  }
}
</script>

<style lang="scss" scoped>
$table-header-height: 42px;
.base-table {
  display: flex;
  flex-flow: column;
  ::v-deep .el-table {
    th {
      font-size: $font-size-main;
      font-weight: normal;
      color: $color-font-title;
      background: $color-bg-select;
    }
    .el-table__fixed-header-wrapper,
    .el-table__header-wrapper {
      .header-row {
        height: $table-header-height;
        th {
          padding: 0 !important;
        }
      }
    }
    .el-table-column--selection .cell {
      padding-right: 10px;
    }

    &.line-height-level-1 {
      .el-table__row {
        height: 36px;
      }
    }
    &.line-height-level-2 {
      .el-table__row {
        height: 41px;
      }
    }
    &.line-height-level-3 {
      .el-table__row {
        height: 46px;
      }
    }
    .el-table__body-wrapper,
    .el-table__fixed-body-wrapper {
      font-size: 13px;
      .el-table__row {
        td {
          padding: 0 !important;
          overflow: hidden;
        }
      }
    }
    .el-table__fixed {
      height: 100% !important;
      .el-table__fixed-body-wrapper {
        height: calc(100% - #{$table-header-height}) !important; // 解决滚动条透明问题
      }
    }
    .el-table__fixed-right {
      height: 100% !important;
      right: 0 !important;
      .el-table__fixed-body-wrapper {
        height: calc(100% - #{$table-header-height}) !important; // 解决滚动条透明问题
      }
    }
    .ps__rail-x,
    .ps__rail-y {
      z-index: 10;
    }
    .el-table__expand-icon {
      margin-right: 0;
    }
  }
  .pagination-wrap {
    display: flex;
    align-items: center;
    .table-pagination {
      padding: 10px 17px;
      ::v-deep .el-pagination__total {
        margin-left: 10px;
        line-height: 29px;
      }
    }
    .svg-icon {
      font-size: 18px;
    }
  }
  .show-pointer ::v-deep {
    .el-table__body-wrapper,
    .el-table__fixed-body-wrapper {
      .el-table__row {
        cursor: pointer;
      }
    }
  }
}
.command-wrap {
  &.is-active {
    background: $color-bg-select;
  }
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
  .svg-icon {
    font-size: 18px;
    margin-right: 8px;
  }
}
</style>
